import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { resolvePath, useLocation, useNavigate } from 'react-router-dom';

import { ActiveLine, ButtonWrapper, HeaderNavButton, Holder, Wrapper } from './styles';

export interface TabProp {
    label: string;
    value: string;
    disabled?: boolean;
}

interface NavigationBarProps {
    small?: boolean;
    onTabClick?: (tab: TabProp) => void;
    isMobile?: boolean;
    tabs: TabProp[];
    activeTab?: TabProp;
    className?: string;
    basePath?: string;
    children?: React.ReactNode;
    disableNavigate?: boolean;
}

interface ActiveTabProps {
    width: number;
    x: number;
    opacity: number;
}

export const HomeNavigationBar = ({
    isMobile,
    tabs,
    onTabClick,
    basePath,
    className,
    children,
    small,
    disableNavigate,
    activeTab,
}: NavigationBarProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [activeLine, setActiveLine] = useState<ActiveTabProps | undefined>(
        isMobile ? { width: 98, x: 20, opacity: 0 } : { width: 89, x: 0, opacity: 0 },
    );

    const { pathname } = useLocation();

    useLayoutEffect(() => {
        const resolvedPath = pathname?.replace(`${basePath}/`, '');
        if (resolvedPath && !disableNavigate) {
            if (activeTab && activeTab.value !== resolvedPath) {
                onTabClick?.(activeTab);
            }
        }
    }, [activeTab, basePath, disableNavigate, onTabClick, pathname]);

    const navigate = useNavigate();

    const animateLine = (target: HTMLElement) => {
        if (!wrapperRef.current) return;
        const position = target.getBoundingClientRect();
        const parentPosition = wrapperRef.current.getBoundingClientRect();

        setActiveLine({
            width: position.width,
            x: position.x + wrapperRef.current.scrollLeft - parentPosition.x,
            opacity: 1,
        });
    };

    const updateActiveLine = useCallback(
        ({ width, x }) => {
            setActiveLine({
                width,
                x,
                opacity: 1,
            });
        },
        [setActiveLine],
    );

    useEffect(() => {
        if (!wrapperRef.current) return;

        const node = wrapperRef.current.querySelector(`[data-active="true"]`) as HTMLElement;
        if (!node) return;

        const updateActiveLineCallback = () => {
            if (!wrapperRef.current) return;
            const position = node.getBoundingClientRect();
            const parentPosition = wrapperRef.current.getBoundingClientRect();
            updateActiveLine({
                width: position.width,
                x: position.x + wrapperRef.current.scrollLeft - parentPosition.x,
            });
        };

        const timer = setTimeout(() => {
            updateActiveLineCallback();
        }, 500);

        window.addEventListener('resize', updateActiveLineCallback);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateActiveLineCallback);
        };
        // update change active line if pathname changes
    }, [updateActiveLine, pathname, activeTab?.value]);

    const handleClick = (tab: TabProp, target: HTMLElement) => {
        if (!disableNavigate) {
            const resolved = resolvePath(tab.value, basePath);
            navigate(resolved.pathname);
        }
        onTabClick?.(tab);
        animateLine(target);
    };
    const pathEnd = pathname.split('/').pop();

    return (
        <Wrapper className={className}>
            <Holder>
                <ButtonWrapper ref={wrapperRef} $small={small}>
                    {tabs.map((type, i) => {
                        const typeLast = type.value.split('/').pop();
                        const isActive =
                            pathEnd === typeLast ||
                            (pathname === basePath && i === 0) ||
                            activeTab?.value === type.value;

                        return (
                            <HeaderNavButton
                                $small={small}
                                $disabled={type.disabled}
                                key={`tab-${type.value}`}
                                data-active={isActive}
                                $isActive={isActive}
                                onClick={(e) => {
                                    handleClick(type, e.target as HTMLElement);
                                }}
                            >
                                {type.label}
                            </HeaderNavButton>
                        );
                    })}

                    <ActiveLine
                        animate={activeLine}
                        transition={{ duration: 0.9, ease: [0.68, -0.4, 0.32, 1.4], opacity: { delay: 0.5 } }}
                    />
                </ButtonWrapper>
                {children}
            </Holder>
        </Wrapper>
    );
};
